'use client'

import { getAuthFlowLoadingSelector } from '@patrianna/shared-store/auth'
import { getCookie } from '@patrianna/shared-utils/cookie'
import { skipOtpSelectorWithMandatoryAndClosed } from '@patrianna-payments/shared-store/skip-otp'
import { createSelector } from '@reduxjs/toolkit'

import { IS_BRAND_PULSZ, IS_BRAND_PULSZBINGO } from 'config/brands'
import { isLegalsUpdatedAvailableSelector, sweepstakeEnabledSelector } from 'store/modules/appConfig/selectors'
import { checkUserBonuses } from 'store/modules/bonusesFlow/actions'
import {
  bonusesLoadedSelector,
  isShownRewardDialogSelector,
  isDailyBonusAvailableSelector,
  isManualBonusAvailableSelector,
} from 'store/modules/bonusesFlow/selectors'
import { getJackpotMyStashWinSelector } from 'store/modules/jackpots/selectors'
import { offersLoadedSelector, isOffersAvailableSelector } from 'store/modules/shop/selectors'
import { freeSpinsLoadedSelector, getGamesWithFreeSpinsSelector } from 'store/modules/slotGameFlow/selectors'
import {
  checkLegalsModalVisibility,
  openLoyaltyInactivityDialog,
  openWelcomeEmailDialogWithDelay,
} from 'store/modules/user/actions'
import {
  getAcceptedSrVersionSelector,
  getUserModeSelector,
  isLoggedInSelector,
  isUpdateingLegalsSelector,
  needToVerifyEmailSelector,
  isLoyaltyInactivityAvailableSelector,
} from 'store/modules/user/selectors'
import { getItemFormLocalStorageSelector } from 'utils/localStorage'

import config from './config.json'

import type { ReduxState } from 'src/store'

const BLOCK_SESSION_DIALOG_COOKIE = 'sdb'

const disabledSelector = createSelector(
  isLoggedInSelector,
  skipOtpSelectorWithMandatoryAndClosed,
  needToVerifyEmailSelector,
  offersLoadedSelector,
  freeSpinsLoadedSelector,
  bonusesLoadedSelector,
  getAuthFlowLoadingSelector,
  (loggedIn, skipOtp, emailNotVerified, offersLoaded, freeSpinsLoaded, bonusesLoaded, isAuthLoading) =>
    !skipOtp || emailNotVerified || !loggedIn || !offersLoaded || !freeSpinsLoaded || !bonusesLoaded || isAuthLoading,
)

const currencyModeSelector = createSelector(sweepstakeEnabledSelector, (sweepstakeEnabled) =>
  sweepstakeEnabled ? 'SC' : 'GC',
)

const flagsSelector = createSelector(
  (state: ReduxState) => state,
  (state) => {
    const isScAvailable = sweepstakeEnabledSelector(state)
    const acceptedSrVersion = getAcceptedSrVersionSelector(state)
    const isDailyBonusAvailable = isDailyBonusAvailableSelector(state)
    const isBonusesLoaded = bonusesLoadedSelector(state)
    const isOffersAvailable = isOffersAvailableSelector(state)
    const myStashWin = getJackpotMyStashWinSelector(state)
    const isLegalsUpdatedAvailable = isLegalsUpdatedAvailableSelector(state)
    const isLegalsUpdating = isUpdateingLegalsSelector(state)
    const isRewardAvailable = isManualBonusAvailableSelector(state)
    const isLoyaltyInactivityAvailable = isLoyaltyInactivityAvailableSelector(state)
    const freeSpins = getGamesWithFreeSpinsSelector(state)
    const userMode = getUserModeSelector(state)
    const isShownRewardDialog = isShownRewardDialogSelector(state)
    const isFreeSpinsAvailable = freeSpins?.length > 0

    const cookies = typeof document !== 'undefined' ? document.cookie : undefined
    const isPreviewSessionAvailable = IS_BRAND_PULSZ && userMode?.includes?.('sweepstake')
    // open dialog if timer null
    const timerId = getItemFormLocalStorageSelector(state, 'WELCOME_EMAIL_DIALOG_TIMER_ID')
    const switchCurrencyModalCookie = Boolean(getCookie('SWITCH_CURRENCY_MODAL', cookies))
    const isBingoCashPrizesAvailable = getCookie('SHOW_BINGO_CASH_PRIZES_DIALOG', cookies) && IS_BRAND_PULSZBINGO
    const isSessionDialogsBlocked = !!getCookie(BLOCK_SESSION_DIALOG_COOKIE, cookies)

    const flags = {
      isQuickStart: isScAvailable && (!acceptedSrVersion || switchCurrencyModalCookie),
      isPreviewSessionAvailable,
      isLegalsUpdatedAvailable,
      isDailyBonusAvailable,
      isJackpotMyStashUnlocked: !!myStashWin.amount,
      isFreeSpinsAvailable: !isLegalsUpdating && isFreeSpinsAvailable && (isShownRewardDialog || !isRewardAvailable),
      isRewardAvailable,
      isLoyaltyInactivityAvailable,
      isOffersAvailable: !isLegalsUpdating && isBonusesLoaded && isOffersAvailable, // condition to prevent offers to be shown before daily bonuses after acceptLegals() call
      isWelcomeEmailAvailable: !timerId,
      isBingoCashPrizesAvailable,
      ...(IS_BRAND_PULSZBINGO && {
        isDailyBonusAvailable: isDailyBonusAvailable && isBonusesLoaded,
      }),
    }

    if (isSessionDialogsBlocked) {
      return new Proxy(flags, {
        get(target, prop: string) {
          if (prop === 'isLoyaltyInactivityAvailable') {
            return target[prop as keyof typeof target]
          }

          return false
        },
      })
    }

    return flags
  },
)

const sessionDialogsSelector = createSelector(
  disabledSelector,
  currencyModeSelector,
  flagsSelector,
  (disabled, currencyMode, flags) => ({ disabled, currencyMode, flags }),
)

export default {
  config,
  stateSelector: sessionDialogsSelector,
  actionsMap: {
    checkUserBonuses,
    checkLegalsModalVisibility,
    openWelcomeEmailDialogWithDelay,
    openLoyaltyInactivityDialog,
  } as any, // Temporary fix before updating types in patrianna-packages
}
